import { GeoJsonLayer, IconLayer } from '@deck.gl/layers/typed';
import { Layer } from '@deck.gl/core/typed';
import { HighlightAlt as Area, Place, Timeline as Segment, Draw } from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { DrawLineStringMode, DrawPointMode, DrawPolygonMode } from '@nebula.gl/edit-modes';
import { MapTool } from 'components/maps/LayersToolbar/const';
import { MapContext } from 'components/maps/MapContext';
import { useHotKey } from 'kineticUtils/hooks';
import { EditableGeoJsonLayer } from '@nebula.gl/layers';
import React, { CSSProperties, memo, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useMapToolboxStyles from './styles';
import { MapToolboxProps } from './types';
import { EditableGeoJsonLayerProps } from '../BaseMap';
import { FeatureCollection } from '@turf/helpers';
import clsx from 'clsx';

const MAP_TOOL_MODES = {
  [MapTool.AREA]: () => DrawPolygonMode,
  [MapTool.POINT]: () => DrawPointMode,
  [MapTool.SEGMENT]: () => DrawLineStringMode,
} as const;

type SupportedMapTool = keyof typeof MAP_TOOL_MODES;
type SupportedMapToolMode = ReturnType<(typeof MAP_TOOL_MODES)[SupportedMapTool]>;

const DEFAULT_FILL_COLOR: [number, number, number, number] = [0, 0, 0, 175];
const DEFAULT_LINE_COLOR: [number, number, number, number] = [0, 0, 0, 255];
const DEFAULT_LINE_WIDTH: number = 14;

function defaultSVGIcon() {
  return `<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#ff1600">
    <path d="M0 0h24v24H0z" fill="none"/><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
  </svg>`;
}

function svgToDataURL(svg?: string) {
  if (!svg) {
    return;
  }
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

const DEFAULT_FEATURE: FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

const POINT_LAYER_ID = 'toolbox-point-layer-id';
const LINE_LAYER_ID = 'toolbox-line-layer-id';
const EXTRUDED_POINT_LAYER_ID = 'toolbox-extruded-point-layer-id';

export const MapToolbox: React.FunctionComponent<MapToolboxProps> = ({
  entityId,
  geometry: entityGeometry,
  features,
  verticalPosition,
  horizontalPosition,
  verticalPadding,
  horizontalPadding,
  onLocationSelection,
  onFeatureLocationSelection,
  svgIcon,
  svgIconWidth,
  svgIconHeight,
  svgSize,
  iconOffset,
  geoJsonStyles,
  supportsArea = true,
  supportsPoint = true,
  supportsLine = true,
  order = 1,
  elevation = 0,
  extruded = false,
  multiple = false,
  disableContinueDrawing = false,
  setPropActiveMapTool,
}) => {
  const buttonGroupStyle: CSSProperties = {
    position: 'absolute',
    top: verticalPosition === 'top' ? `${verticalPadding ?? 10}px` : 'unset',
    bottom: verticalPosition === 'bottom' ? `${verticalPadding ?? 10}px` : 'unset',
    left: horizontalPosition === 'left' ? `${horizontalPadding ?? 10}px` : 'unset',
    right: horizontalPosition === 'right' ? `${horizontalPadding ?? 10}px` : 'unset',
  };

  const { t } = useTranslation(['app']);
  const classes = useMapToolboxStyles();
  const {
    setExtraDeckGlLayers,
    setActiveMapTool: setContextActiveMapTool,
    setHiddenMarkers,
  } = useContext(MapContext);

  const [mode, setMode] = useState<SupportedMapToolMode | null>(null);
  const [view, setView] = useState<SupportedMapTool | null>(null);
  const [selectedFeatureIndexes] = useState([]);
  const [currentFeatures, setCurrentFeatures] = React.useState(features ?? DEFAULT_FEATURE);

  const updateActiveMapToolInAllScopes = useCallback(
    (tool: MapTool | null) => {
      setContextActiveMapTool(tool);
      setPropActiveMapTool?.(tool);
    },
    [setContextActiveMapTool, setPropActiveMapTool]
  );

  useEffect(() => {
    if (entityId) {
      setHiddenMarkers((prev) => [...prev, entityId]);
    }
  }, [entityId, setHiddenMarkers]);

  useHotKey({
    key: 'a',
    alt: true,
    ctrl: true,
    callback: () => {
      updateActiveMapToolInAllScopes(MapTool.AREA);
      setView(MapTool.AREA);
      setMode(MAP_TOOL_MODES[MapTool.AREA]);
    },
  });

  useHotKey({
    key: 'p',
    alt: true,
    ctrl: true,
    callback: () => {
      updateActiveMapToolInAllScopes(MapTool.POINT);
      setView(MapTool.POINT);
      setMode(MAP_TOOL_MODES[MapTool.POINT]);
    },
  });

  useHotKey({
    key: 's',
    alt: true,
    ctrl: true,
    callback: () => {
      updateActiveMapToolInAllScopes(MapTool.SEGMENT);
      setView(MapTool.SEGMENT);
      setMode(MAP_TOOL_MODES[MapTool.SEGMENT]);
    },
  });

  const removeLayers = (data: (Layer<{}> | EditableGeoJsonLayer)[]) =>
    data.filter(
      (layer) =>
        layer.id !== POINT_LAYER_ID &&
        layer.id !== EXTRUDED_POINT_LAYER_ID &&
        layer.id !== LINE_LAYER_ID
    );

  const removeFeatureLayer = useCallback(() => {
    setExtraDeckGlLayers((prev) => removeLayers(prev));
  }, [setExtraDeckGlLayers]);

  useEffect(() => {
    // If the geometry was set to undefined (e.g. when user clears the location from the field), remove the POINT_LAYER_ID layer
    if (!entityGeometry && !features) {
      removeFeatureLayer();
      return;
    }
    const geometry = features?.features?.[0]?.geometry ?? entityGeometry;
    if (geometry?.type === 'Point') {
      setExtraDeckGlLayers((prev) => {
        const toSave = removeLayers(prev);
        const layer = new IconLayer({
          id: POINT_LAYER_ID,
          data: [geometry],
          getIcon: () =>
            ({
              url: svgToDataURL(svgIcon) ?? svgToDataURL(defaultSVGIcon()),
              width: svgIconWidth ?? 24,
              height: svgIconHeight ?? 24,
            } as any),
          getPixelOffset: svgIcon && iconOffset ? iconOffset : [0, -15],
          getPosition: (d) => d.coordinates,
          getSize: svgSize ?? 40,
          pickable: false,
          order,
        });
        return [...toSave, layer];
      });
      setCurrentFeatures(DEFAULT_FEATURE);
    } else if (
      geometry?.type === 'LineString' ||
      geometry?.type === 'MultiLineString' ||
      geometry?.type === 'Polygon' ||
      geometry?.type === 'MultiPolygon'
    ) {
      const data = features ?? {
        type: 'Feature',
        geometry: geometry,
        properties: {
          elevation,
          fillColor: geoJsonStyles?.fillColor ?? DEFAULT_FILL_COLOR,
          lineColor: geoJsonStyles?.lineColor ?? DEFAULT_LINE_COLOR,
        },
      };
      setExtraDeckGlLayers((prev) => {
        const toSave = removeLayers(prev);
        const layer = new GeoJsonLayer({
          id: LINE_LAYER_ID,
          data,
          getFillColor: (d) =>
            d?.properties?.fillColor ?? geoJsonStyles?.fillColor ?? DEFAULT_FILL_COLOR,
          getLineColor: (d) =>
            d?.properties?.lineColor ?? geoJsonStyles?.lineColor ?? DEFAULT_LINE_COLOR,
          getLineWidth: geoJsonStyles?.lineWidth ?? DEFAULT_LINE_WIDTH,
          lineWidthMinPixels: 4,
          order,
        });
        const extrudedLayer = extruded
          ? new GeoJsonLayer({
              id: EXTRUDED_POINT_LAYER_ID,
              data,
              getFillColor: (d) => d?.properties?.fillColor,
              getLineColor: (d) => d?.properties?.lineColor,
              getLineWidth: geoJsonStyles?.lineWidth ?? DEFAULT_LINE_WIDTH,
              lineWidthMinPixels: 4,
              order,
              getElevation: (d) => d?.properties?.elevation,
              extruded,
              filled: false,
              stroked: true,
              wireframe: true,
              lineWidthScale: 1,
              lineWidthUnits: 'meters',
            })
          : null;
        if (extrudedLayer) {
          toSave.push(extrudedLayer);
        }
        toSave.push(layer);
        return toSave;
      });
      !multiple && setCurrentFeatures(DEFAULT_FEATURE);
    }
  }, [
    entityGeometry,
    geoJsonStyles?.fillColor,
    geoJsonStyles?.lineColor,
    geoJsonStyles?.lineWidth,
    iconOffset,
    setExtraDeckGlLayers,
    svgIcon,
    svgIconHeight,
    svgIconWidth,
    svgSize,
    order,
    removeFeatureLayer,
    elevation,
    extruded,
    features,
    multiple,
  ]);

  const onLocationSelectionChange = useCallback(
    (data) => {
      const firstGeometry = data?.features[0]?.geometry;
      if (!firstGeometry) {
        return;
      }
      removeFeatureLayer();
      if (multiple) {
        onFeatureLocationSelection?.(data);
      } else {
        onLocationSelection?.(firstGeometry);
      }
      setMode(null);
      updateActiveMapToolInAllScopes(null);
      setView(null);
    },
    [
      multiple,
      onFeatureLocationSelection,
      onLocationSelection,
      removeFeatureLayer,
      updateActiveMapToolInAllScopes,
    ]
  );

  useEffect(() => {
    if (!mode) {
      // remove the layer if we have no mode.
      setExtraDeckGlLayers((prev) => {
        const toSave = [...prev];
        const index = toSave.findIndex((layer) => layer.id === 'map-toolbox');
        if (index > -1) {
          toSave.splice(index, 1);
        }
        return toSave;
      });
      return;
    }

    setExtraDeckGlLayers((prev) => {
      const toSave = [...prev];

      const layer = new EditableGeoJsonLayer({
        id: 'map-toolbox',
        data: currentFeatures,
        mode,
        selectedFeatureIndexes,
        onEdit: (updates) => {
          // Only update when points are updated
          if (updates.editType !== 'addFeature') {
            return;
          }
          const data = { ...updates.updatedData };

          if (!multiple && data.features.length > 1) {
            data.features.shift();
          }
          setCurrentFeatures(data);
          onLocationSelectionChange(data);
        },
        order: 4,
      } as EditableGeoJsonLayerProps);

      const index = toSave.findIndex((layer) => layer.id === 'map-toolbox');
      if (index > -1) {
        toSave.splice(index, 1);
      }
      toSave.push(layer);
      return toSave;
    });
  }, [
    setExtraDeckGlLayers,
    mode,
    selectedFeatureIndexes,
    currentFeatures,
    view,
    onLocationSelectionChange,
    multiple,
  ]);

  const handleViewChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, nextView: keyof typeof MAP_TOOL_MODES) => {
      event.stopPropagation();
      event.preventDefault();
      removeFeatureLayer();
      updateActiveMapToolInAllScopes(nextView);
      setView(nextView);
      setMode(MAP_TOOL_MODES[nextView]);
    },
    [removeFeatureLayer, updateActiveMapToolInAllScopes]
  );

  const handleChange = useCallback(
    (event: React.MouseEvent<HTMLElement>, nextView: keyof typeof MAP_TOOL_MODES) => {
      handleViewChange(event, nextView);
      setCurrentFeatures(DEFAULT_FEATURE);
      onFeatureLocationSelection?.();
    },
    [handleViewChange, onFeatureLocationSelection]
  );

  const handleContinueDrawing = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const nextView = supportsLine ? MapTool.SEGMENT : MapTool.AREA;
      handleViewChange(event, nextView);
      setCurrentFeatures(features ?? DEFAULT_FEATURE);
    },
    [features, handleViewChange, supportsLine]
  );

  return (
    <div style={buttonGroupStyle}>
      {multiple && (
        <Tooltip title={t('continueDrawing')}>
          <ToggleButton
            className={clsx(classes.buttonGroup, classes.drawButton)}
            value={''}
            disabled={disableContinueDrawing || mode !== null}
            onClick={handleContinueDrawing}
            size='small'
          >
            <Draw fontSize='small' />
          </ToggleButton>
        </Tooltip>
      )}
      <ToggleButtonGroup
        orientation='vertical'
        value={view}
        exclusive
        onChange={handleChange}
        size='small'
        className={classes.buttonGroup}
      >
        {supportsPoint && (
          <Tooltip title={t('pointTool')}>
            <ToggleButton value={MapTool.POINT}>
              <Place fontSize='small' />
            </ToggleButton>
          </Tooltip>
        )}
        {supportsLine && (
          <Tooltip title={t('segmentTool')}>
            <ToggleButton value={MapTool.SEGMENT}>
              <Segment fontSize='small' />
            </ToggleButton>
          </Tooltip>
        )}
        {supportsArea && (
          <Tooltip title={t('areaTool')}>
            <ToggleButton value={MapTool.AREA}>
              <Area fontSize='small' />
            </ToggleButton>
          </Tooltip>
        )}
      </ToggleButtonGroup>
    </div>
  );
};

export default memo(MapToolbox);
